import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { varinymAPI } from '../lib/varinym-api';
import type { BulkJob, TransformInfo, TransformPreview } from '../types';

const POLLING_STATUSES = new Set(['processing', 'queued', 'analyzing']);

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return '✓';
    case 'failed':
      return '✗';
    case 'processing':
    case 'analyzing':
      return '⟳';
    case 'partial':
      return '⚠';
    case 'awaiting_confirmation':
      return '⏸';
    default:
      return '○';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'text-success bg-success/10';
    case 'failed':
      return 'text-destructive bg-destructive/10';
    case 'processing':
    case 'analyzing':
      return 'text-info bg-info/10';
    case 'partial':
    case 'awaiting_confirmation':
      return 'text-warning bg-warning/10';
    default:
      return 'text-muted-foreground bg-muted';
  }
};

export function JobDetails() {
  const { jobId } = useParams<{ jobId: string }>();

  const { data: jobData, isLoading } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => varinymAPI.getJobStatus(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      // awaiting_confirmation is a human-in-the-loop gate, not a transient state —
      // it can sit here indefinitely, so stop polling until the user confirms.
      if (data?.job?.status && POLLING_STATUSES.has(data.job.status)) {
        return 2500;
      }
      return false;
    },
  });

  const { data: failedData } = useQuery({
    queryKey: ['job-results', jobId],
    queryFn: () => varinymAPI.getJobResults(jobId!),
    enabled: !!jobId && (jobData?.job?.failed_items ?? 0) > 0,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!jobData?.job) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-foreground mb-6">Job Not Found</h1>
        <Card>
          <p className="text-muted-foreground">The job you're looking for doesn't exist.</p>
          <Link to="/bulk/jobs" className="link-bare">
            <Button className="mt-4">Back to Job History</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const job = jobData.job;
  const transform = jobData.transform;
  const preview = jobData.preview;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/bulk/jobs" className="text-sm text-muted-foreground hover:text-foreground mb-2 inline-block">
            ← Back to Jobs
          </Link>
          <h1 className="text-2xl font-bold text-foreground">Job Details</h1>
        </div>
        <div className={`px-4 py-2 rounded-lg ${getStatusColor(job.status)}`}>
          <span className="text-2xl mr-2">{getStatusIcon(job.status)}</span>
          <span className="font-semibold uppercase">{job.status.replace(/_/g, ' ')}</span>
          {(job.status === 'processing' || job.status === 'analyzing') && (
            <span className="ml-1 animate-pulse">...</span>
          )}
        </div>
      </div>

      <div className="space-y-6">
        {/* Job Info */}
        <Card title="Job Information">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Job ID</dt>
              <dd className="mt-1 text-sm text-foreground font-mono break-all">{job.job_id}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Operation</dt>
              <dd className="mt-1 text-sm text-foreground">{job.operation}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">Created</dt>
              <dd className="mt-1 text-sm text-foreground">
                {new Date(job.created_at * 1000).toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                {job.completed_at ? 'Completed' : 'Last Updated'}
              </dt>
              <dd className="mt-1 text-sm text-foreground">
                {new Date((job.completed_at || job.updated_at) * 1000).toLocaleString()}
              </dd>
            </div>
            {job.file_key && (
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-muted-foreground">File</dt>
                <dd className="mt-1 text-sm text-foreground font-mono break-all">{job.file_key}</dd>
              </div>
            )}
            {transform && (
              <>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Column</dt>
                  <dd className="mt-1 text-sm text-foreground font-mono">{transform.column}</dd>
                </div>
                {transform.context && (
                  <div>
                    <dt className="text-sm font-medium text-muted-foreground">Context</dt>
                    <dd className="mt-1 text-sm text-foreground">{transform.context}</dd>
                  </div>
                )}
              </>
            )}
          </dl>
        </Card>

        {transform ? (
          <TransformPanel jobId={jobId!} job={job} transform={transform} preview={preview} />
        ) : (
          <ClassicProgress job={job} />
        )}

        {/* Failed Items */}
        {failedData && failedData.failed_rows && failedData.failed_rows.length > 0 && (
          <Card
            title="Failed Items"
            actions={
              <Button size="sm" variant="secondary">
                Download Failed Rows
              </Button>
            }
          >
            {failedData.truncated && (
              <p className="text-sm text-muted-foreground mb-3">
                Showing {failedData.failed_rows.length.toLocaleString()} of{' '}
                {failedData.failed_count.toLocaleString()} failed rows.
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Line
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      User ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                      Reason
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {failedData.failed_rows.map((row, index) => (
                    <tr key={index} className="hover:bg-accent/50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                        {row.line}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-foreground">
                        {row.user_id || '(empty)'}
                      </td>
                      <td className="px-6 py-4 text-sm text-destructive">{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Error Message */}
        {job.error_message && (
          <Card>
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-destructive text-xl">⚠</span>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-destructive">Error</h3>
                  <div className="mt-2 text-sm text-destructive">
                    <pre className="whitespace-pre-wrap">{job.error_message}</pre>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function ClassicProgress({ job }: { job: BulkJob }) {
  const progressPercent = job.total_items > 0 ? (job.processed_items / job.total_items) * 100 : 0;
  const successRate = job.processed_items > 0 ? (job.successful_items / job.processed_items) * 100 : 0;

  return (
    <Card title="Progress">
      <div className="space-y-4">
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="font-medium text-foreground">
              {job.processed_items} / {job.total_items} items processed
            </span>
            <span className="text-muted-foreground">{progressPercent.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-300 ${
                job.status === 'processing' ? 'bg-info animate-pulse' : 'bg-primary'
              }`}
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 pt-4">
          <div className="bg-success/10 p-4 rounded-lg">
            <div className="text-sm text-success font-medium">Successful</div>
            <div className="text-2xl font-bold text-success">{job.successful_items}</div>
            <div className="text-xs text-success mt-1">{successRate.toFixed(1)}%</div>
          </div>
          <div className="bg-destructive/10 p-4 rounded-lg">
            <div className="text-sm text-destructive font-medium">Failed</div>
            <div className="text-2xl font-bold text-destructive">{job.failed_items}</div>
            <div className="text-xs text-destructive mt-1">
              {job.processed_items > 0
                ? ((job.failed_items / job.processed_items) * 100).toFixed(1)
                : 0}
              %
            </div>
          </div>
          <div className="bg-muted p-4 rounded-lg">
            <div className="text-sm text-muted-foreground font-medium">Remaining</div>
            <div className="text-2xl font-bold text-foreground">
              {job.total_items - job.processed_items}
            </div>
            {job.status === 'processing' && job.processed_items > 0 && (
              <div className="text-xs text-muted-foreground mt-1">
                Est. {Math.ceil((job.total_items - job.processed_items) / 100)} min
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

function TransformPanel({
  jobId,
  job,
  transform,
  preview,
}: {
  jobId: string;
  job: BulkJob;
  transform: TransformInfo;
  preview?: TransformPreview | null;
}) {
  const queryClient = useQueryClient();
  const [confirmLocked, setConfirmLocked] = useState(false);
  const [downloadError, setDownloadError] = useState<{ kind: 'not_ready' | 'expired' | 'server'; message: string } | null>(null);

  const confirmMutation = useMutation({
    mutationFn: () => varinymAPI.confirmTransform(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
    },
    onError: () => {
      // 409 means our local state is stale (double-click, another tab already
      // confirmed) — stop letting the user retry, just refetch the real state.
      setConfirmLocked(true);
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
    },
  });

  const downloadMutation = useMutation({
    mutationFn: async () => {
      const res = await varinymAPI.downloadTransformOutput(jobId);
      if (!res.ok) {
        const body = await res.json().catch(() => ({ msg: 'Download failed' }));
        if (res.status === 410) {
          throw { kind: 'expired' as const, message: body.msg || 'This export has expired.' };
        }
        if (res.status === 409) {
          throw { kind: 'not_ready' as const, message: body.msg || 'Output is not ready yet.' };
        }
        throw { kind: 'server' as const, message: body.msg || 'Download failed.' };
      }
      return res.blob();
    },
    onSuccess: (blob) => {
      setDownloadError(null);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${jobId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    },
    onError: (err: any) => {
      setDownloadError({ kind: err.kind || 'server', message: err.message || 'Download failed.' });
    },
  });

  const now = Date.now() / 1000;
  const outputExpired = !!transform.output_expires_at && transform.output_expires_at <= now;
  const outputAvailable = !!transform.output_expires_at && !outputExpired;

  return (
    <>
      {job.status === 'analyzing' && (
        <Card title="Analyzing">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            Parsing the file, counting rows, and validating the target column exists...
          </div>
        </Card>
      )}

      {job.status === 'awaiting_confirmation' && preview && (
        <Card
          title="Preview"
          actions={
            <Button
              onClick={() => confirmMutation.mutate()}
              isLoading={confirmMutation.isPending}
              disabled={confirmLocked}
            >
              Confirm & Run
            </Button>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {preview.total_rows.toLocaleString()} rows found
              {transform.distinct_values != null && ` · ${transform.distinct_values.toLocaleString()} distinct values`}
              {preview.blank_rows > 0 && ` · ${preview.blank_rows.toLocaleString()} blank rows`}
              {preview.empty_target_rows > 0 && ` · ${preview.empty_target_rows.toLocaleString()} rows with an empty target value`}
            </p>
            {confirmLocked && (
              <p className="text-sm text-warning">
                This job is no longer awaiting confirmation — it may have already been confirmed
                (e.g. in another tab). Refreshing the status above.
              </p>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border text-sm">
                <thead>
                  <tr>
                    {preview.columns.map((col, i) => (
                      <th
                        key={col}
                        className={`px-4 py-2 text-left text-xs font-medium uppercase ${
                          i === preview.column_index ? 'text-primary' : 'text-muted-foreground'
                        }`}
                      >
                        {col}
                        {i === preview.column_index && ' (target)'}
                      </th>
                    ))}
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-primary">After</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {preview.rows.map((row, i) => (
                    <tr key={i}>
                      {row.before.map((cell, j) => (
                        <td
                          key={j}
                          className={`px-4 py-2 font-mono whitespace-nowrap ${
                            j === preview.column_index ? 'text-foreground font-medium' : 'text-muted-foreground'
                          }`}
                        >
                          {cell || '(blank)'}
                        </td>
                      ))}
                      <td className="px-4 py-2 font-mono whitespace-nowrap text-foreground">
                        {row.after ?? <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {(job.status === 'processing' ||
        job.status === 'completed' ||
        job.status === 'partial' ||
        job.status === 'failed') && <ClassicProgress job={job} />}

      {transform.output_expires_at != null && (
        <Card title="Download">
          {outputAvailable ? (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Available until {new Date(transform.output_expires_at * 1000).toLocaleString()}.
              </p>
              <Button
                onClick={() => downloadMutation.mutate()}
                isLoading={downloadMutation.isPending}
              >
                Download CSV
              </Button>
            </div>
          ) : (
            <div>
              <Button disabled className="opacity-50 cursor-not-allowed">
                Download CSV
              </Button>
              <p className="text-sm text-muted-foreground mt-2">
                This export has expired (90-minute retention). Re-run the transform to get a new one.
              </p>
            </div>
          )}
          {downloadError && (
            <p className={`text-sm mt-3 ${downloadError.kind === 'server' ? 'text-destructive' : 'text-warning'}`}>
              {downloadError.message}
            </p>
          )}
        </Card>
      )}
    </>
  );
}
